import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { jsx as _jsx } from "react/jsx-runtime";

let _ = t => t,
    _t,
    _t2;

/* hs-eslint ignored failing-rules */

/* eslint-disable react-hooks/exhaustive-deps */
import breakpoint from 'pricing-pages-ui/constants/Breakpoints';
import { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { ANNUAL, MONTHLY } from 'self-service-api/constants/Terms';
import styled from 'styled-components';
import UIFlex from 'UIComponents/layout/UIFlex';
import UILoadingSpinner from 'UIComponents/loading/UILoadingSpinner';
import { INTERSTITIAL } from '../../constants/tracking/Locations';
import { PlanBuilderContext } from '../../context/PlanBuilderContext';
import { useSortedProductConfigurations } from '../../hooks/useSortedProductConfigurations';
import { useAppSelector } from '../../reducers';
import { isUngatedForSeatsSelector } from '../../selectors/GatingSelectors';
import { hasPricingAndPurchasingFailedSelector, hasPricingAndPurchasingSucceededSelector } from '../../selectors/LoadingStateSelectors';
import { commonAvailableTermsSelector } from '../../selectors/OfferSelectors';
import { checkoutProductConfigurationsSelector, configsWithSuiteIfPossibleSelector, productConfigurationForSeatsSelector } from '../../selectors/ProductConfigsSelectors';
import { hasProductVersionOrHigherProductLevelInProductInventorySelector, ownsCrmSuiteProductLevel } from '../../selectors/ProductInventorySelectors';
import { crmSuiteProductLevelSelector, isQuantifiableHubSelector, skuIdIsHubSelector, skuIdIsPartOfCrmSuiteSelector } from '../../selectors/ProductsSelectors';
import { useProductConfigsQueryParamsParamsEffect, useQueryParamProductConfigsOrDefault } from '../../utils/planBuilderUtils/PlanBuilderQueryParamUtils';
import { useQueryParamTerm } from '../../utils/urls/queryParamUtils';
const Wrapper = styled.div.withConfig({
  displayName: "PlanBuilder__Wrapper",
  componentId: "oo4rar-0"
})(["max-width:1024px;margin:0 auto;padding:0 15px;", ";"], breakpoint.phone(_t || (_t = _`
    padding: 0;
    width: inherit;
  `)));
const ResponsiveUIFlex = styled(UIFlex).withConfig({
  displayName: "PlanBuilder__ResponsiveUIFlex",
  componentId: "oo4rar-1"
})(["", ";"], breakpoint.cs(_t2 || (_t2 = _`
    flex-direction: column;

    .flex-column {
      margin: 0 auto;
    }
  `)));

const PlanBuilderContent = (_ref) => {
  let {
    initialProductConfigurations = [],
    children,
    trackingSource,
    screen,
    requiredProductLines = [],
    zeroStateCheckoutProductConfigs = [],
    allowUpgradesToCrmSuite = false,
    headerContent = null,
    showDiscountedPricesInUnitsPicker = false
  } = _ref,
      rest = _objectWithoutPropertiesLoose(_ref, ["initialProductConfigurations", "children", "trackingSource", "screen", "requiredProductLines", "zeroStateCheckoutProductConfigs", "allowUpgradesToCrmSuite", "headerContent", "showDiscountedPricesInUnitsPicker"]);

  const isUngatedForSeats = useAppSelector(isUngatedForSeatsSelector);
  const initialProductConfigurationsUsingQueryParams = useQueryParamProductConfigsOrDefault({
    defaultConfigurations: initialProductConfigurations,
    requiredProductLines
  });
  const initialSuiteProductLevel = useSelector(state => crmSuiteProductLevelSelector(state, {
    skuIds: initialProductConfigurationsUsingQueryParams.map(({
      skuId
    }) => skuId)
  }));
  const [suiteProductLevel, setSuiteProductLevel] = useState(initialSuiteProductLevel);
  const selectedOwnedSuiteProductLevel = useSelector(state => {
    if (!suiteProductLevel) return false;
    return ownsCrmSuiteProductLevel(state, {
      productLevel: suiteProductLevel
    });
  });
  const filteredInitialProductConfigurations = useAppSelector(state => {
    return initialProductConfigurationsUsingQueryParams.filter(({
      skuId
    }) => {
      const allowedForCrmSuiteUpgrade = suiteProductLevel && skuIdIsPartOfCrmSuiteSelector(state, {
        skuId,
        suiteProductLevel: suiteProductLevel
      });
      return allowedForCrmSuiteUpgrade || isQuantifiableHubSelector(state, {
        skuId
      }) || !(skuIdIsHubSelector(state, {
        skuId
      }) && hasProductVersionOrHigherProductLevelInProductInventorySelector(state, {
        skuId
      }));
    });
  });
  const [originalProductConfigurations, setProductConfigurations] = useSortedProductConfigurations(filteredInitialProductConfigurations); // On some pages we insert the rest of CRM suite if you own the remaining plans needed for it already

  const productConfigurations = useAppSelector(state => {
    let productConfigs = [...originalProductConfigurations];

    if (allowUpgradesToCrmSuite) {
      productConfigs = configsWithSuiteIfPossibleSelector(state, {
        productConfigurations: productConfigs
      });
    }

    return productConfigurationForSeatsSelector(state, {
      productConfigurations: productConfigs
    });
  });
  const memoizedProductConfigurations = useMemo(() => productConfigurations, [originalProductConfigurations]);
  useProductConfigsQueryParamsParamsEffect(memoizedProductConfigurations);
  const isGoingToInterstitial = isUngatedForSeats && trackingSource !== INTERSTITIAL;
  const checkoutProductConfigurations = useAppSelector(state => {
    const checkoutConfigs = checkoutProductConfigurationsSelector(state, {
      productConfigurations,
      subtractOwnedUnitsIfBuyingNoAdditional: true,
      subtractOwnedSuiteProducts: selectedOwnedSuiteProductLevel && !isGoingToInterstitial
    });
    if (checkoutConfigs.length === 0) return zeroStateCheckoutProductConfigs;
    return checkoutConfigs;
  });
  const {
    queryParamTerm,
    setQueryParamTerm
  } = useQueryParamTerm();
  const commonAllowedQueryParamTerm = useAppSelector(state => {
    const commonAvailableTerms = commonAvailableTermsSelector(state, {
      skuIds: checkoutProductConfigurations.map(({
        skuId
      }) => skuId)
    });
    return queryParamTerm === ANNUAL && commonAvailableTerms.length > 1 ? ANNUAL : MONTHLY;
  });
  return /*#__PURE__*/_jsx(PlanBuilderContext.Provider, {
    value: {
      productConfigurations: memoizedProductConfigurations,
      setProductConfigurations,
      queryParamTerm: queryParamTerm || commonAllowedQueryParamTerm,
      setQueryParamTerm: newTerm => setQueryParamTerm(newTerm),
      requiredProductLines,
      checkoutProductConfigurations,
      trackingSource,
      screen,
      showDiscountedPricesInUnitsPicker,
      setSuiteProductLevel
    },
    children: /*#__PURE__*/_jsxs(Wrapper, Object.assign({}, rest, {
      children: [headerContent, /*#__PURE__*/_jsx(ResponsiveUIFlex, {
        children: children
      })]
    }))
  });
};

const PlanBuilder = props => {
  const hasPricingAndPurchasingSucceeded = useSelector(hasPricingAndPurchasingSucceededSelector);
  const hasPricingAndPurchasingFailed = useSelector(hasPricingAndPurchasingFailedSelector);

  if (hasPricingAndPurchasingSucceeded) {
    return /*#__PURE__*/_jsx(PlanBuilderContent, Object.assign({}, props));
  } else if (hasPricingAndPurchasingFailed) {
    return null;
  } else {
    return /*#__PURE__*/_jsx("div", {
      style: {
        minHeight: 600,
        width: '100%'
      },
      className: "flex-column justify-center align-center",
      children: /*#__PURE__*/_jsx(UILoadingSpinner, {
        size: "medium"
      })
    });
  }
};

export default PlanBuilder;